import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import { Breadcrumb, Form } from 'antd';
import styled from 'styled-components';

import { getCmiDigestCommunities, getCmiDigestPreference, updateCmiDigestPreference } from 'apis/UserAPI';
import Header from 'common/Header';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Loader from 'components/lib/Loader';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleUpdateCmiDigestPreferenceEnabled } from 'store/slices/authSlice';

import { CMI_DIGEST, CMI_DIGEST_FREQUENCY_OPTIONS } from '../../constants';
import CmiDigestAdmin from './CmiDigestAdmin/CmiDigestAdmin';
import CmiDigestForm from './CmiDigestForm';
import { CmiDigestCommunity } from './types';
import { showCmiDigestSelectionRequiredModal } from './validation';

const StyledBreadcrumb = styled(Breadcrumb)`
  font-size: 12px;

  li {
    &:first-child {
      color: var(--dark-blue);
    }
    &:last-child {
      color: var(--text-secondary);
    }
    &.ant-breadcrumb-separator {
      margin-inline: 4px;
    }
  }
`;

const Container = styled.div`
  width: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = styled.div`
  border-bottom: 1px solid var(--line-gray);
`;

const SaveButton = styled(Button)`
  margin-top: 22px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const CMI_DIGEST_INITIAL_VALUES = {
  enabled: false,
  frequency: CMI_DIGEST_FREQUENCY_OPTIONS[0].value,
  competitor_incentives_enabled: true,
  community_reviews_enabled: true,
  ai_instructions: '',
  communities: [] as number[],
};

const breadcrumbs = (
  <StyledBreadcrumb
    separator="·"
    items={[{ title: <Link to="/preferences">Settings</Link> }, { title: 'Email Notifications' }]}
  />
);

const CmiDigest = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const isFurtherAdmin = Boolean(currentUser?.isSuperuser || currentUser?.isStaff);
  const dispatch = useAppDispatch();

  const { showMessage } = useRootContext();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const { data: preference, isLoading: isLoadingPreference } = useQuery('cmiDigestPreference', getCmiDigestPreference, {
    enabled: !isFurtherAdmin,
  });

  const { data: communities = [], isLoading: isLoadingCommunities } = useQuery<CmiDigestCommunity[]>(
    'cmiDigestCommunities',
    getCmiDigestCommunities,
    { enabled: !isFurtherAdmin }
  );

  useEffect(() => {
    // Wait for the accessible-communities list too, otherwise it's still `[]` on first render and
    // every saved community would look stale and get dropped before the real list has a chance to load.
    if (preference && !isLoadingCommunities) {
      const accessibleIds = new Set(communities.map((community) => community.id));
      form.setFieldsValue({
        ...preference,
        // Drop any previously-selected community the user no longer has access to - resubmitting
        // it as-is would otherwise fail backend validation on every save with no way to fix it,
        // since there's no visible checkbox for an inaccessible community to uncheck.
        communities: (preference.communities ?? []).filter((id: number) => accessibleIds.has(id)),
      });
    }
  }, [preference, communities, isLoadingCommunities, form]);

  const handleSave = async (values: any) => {
    if (values.enabled) {
      const missingCommunity = !values.communities || values.communities.length === 0;
      const missingContent = !values.competitor_incentives_enabled && !values.community_reviews_enabled;

      if (missingCommunity || missingContent) {
        showCmiDigestSelectionRequiredModal();
        return;
      }
    }

    setSaving(true);
    try {
      const updatedPreference = await updateCmiDigestPreference(values);
      dispatch(handleUpdateCmiDigestPreferenceEnabled(Boolean(updatedPreference.enabled)));
      showMessage('success', 'CMI Digest settings updated successfully');
    } catch (error) {
      showMessage('error', 'Failed to update CMI Digest settings');
    } finally {
      setSaving(false);
    }
  };

  if (isFurtherAdmin) {
    return (
      <Container>
        <HeaderWrapper>
          <Header
            title="CMI Digest"
            description={CMI_DIGEST.description}
            breadcrumbs={breadcrumbs}
            iconSrc={CMI_DIGEST.icon.url}
            iconBackgroundColor={CMI_DIGEST.icon.backgroundColor}
            iconBackgroundImage={CMI_DIGEST.icon.backgroundImage}
          />
        </HeaderWrapper>
        <CmiDigestAdmin />
      </Container>
    );
  }

  if (isLoadingPreference) {
    return (
      <LoaderWrapper>
        <Loader active />
      </LoaderWrapper>
    );
  }

  return (
    <Container>
      <Header
        title="CMI Digest"
        description={CMI_DIGEST.description}
        breadcrumbs={breadcrumbs}
        iconSrc={CMI_DIGEST.icon.url}
        iconBackgroundColor={CMI_DIGEST.icon.backgroundColor}
        iconBackgroundImage={CMI_DIGEST.icon.backgroundImage}
      >
        <SaveButton type="primary" loading={saving} onClick={() => form.submit()} data-test-id="cmi-digest-save-btn">
          Save
        </SaveButton>
      </Header>
      <StyledForm form={form} initialValues={CMI_DIGEST_INITIAL_VALUES} onFinish={handleSave}>
        <CmiDigestForm form={form} communities={communities} isLoadingCommunities={isLoadingCommunities} />
      </StyledForm>
    </Container>
  );
};

export default CmiDigest;
